// src/components/pages/CalendarioLaboral.jsx
// Página de Calendario Laboral - integrada en el módulo de Control de Personal / RRHH
import React, { useState, useMemo } from 'react';
import {
  FiChevronLeft, FiChevronRight, FiPlus, FiCalendar, FiUsers, FiClock, FiAlertTriangle,
  FiCheckCircle, FiX, FiGift
} from 'react-icons/fi';
import { 
  format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, 
  addMonths, subMonths, isSameMonth, isSameDay, parseISO 
} from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Festivos iniciales (Perú 2026 + ejemplos). Se pueden agregar más.
const INITIAL_HOLIDAYS = [
  { date: '2026-06-29', name: 'San Pedro y San Pablo' },
  { date: '2026-07-28', name: 'Fiestas Patrias - Día 1' },
  { date: '2026-07-29', name: 'Fiestas Patrias - Día 2' },
  { date: '2026-08-30', name: 'Santa Rosa de Lima' },
  { date: '2026-10-08', name: 'Combate de Angamos' },
  { date: '2026-11-01', name: 'Todos los Santos' },
  { date: '2026-12-25', name: 'Navidad' },
];

const getDayKey = (date) => format(date, 'yyyy-MM-dd');

const CalendarioLaboral = ({ users = [], attendanceRecords = [], isAdmin = false }) => {
  // Arranca en el mes de hoy. Antes estaba fijado a junio 2026 "para demo".
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [holidays, setHolidays] = useState(INITIAL_HOLIDAYS);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [filter, setFilter] = useState('todos'); // todos | festivos | trabajados | ausencias

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Lunes
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const selectedStr = format(selectedDate, 'yyyy-MM-dd');

  // Mapa rápido de festivos por fecha
  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach(h => { map[h.date] = h.name; });
    return map;
  }, [holidays]);

  // Estadísticas por día usando attendanceRecords reales
  const dayStats = useMemo(() => {
    const stats = {};
    attendanceRecords.forEach(record => {
      if (!record.date) return;
      if (!stats[record.date]) {
        stats[record.date] = { worked: 0, late: 0, overtime: 0, entries: [] };
      }
      if (record.entryTime) stats[record.date].worked++;
      if (record.lateMinutes > 0) stats[record.date].late++;
      if (record.overtimeMinutes > 0) stats[record.date].overtime++;
      stats[record.date].entries.push(record);
    });
    return stats;
  }, [attendanceRecords]);

  // Días del mes con datos enriquecidos
  const calendarDays = useMemo(() => {
    return allDays.map(day => {
      const key = getDayKey(day);
      const isCurrentMonth = isSameMonth(day, currentDate);
      const isToday = key === todayStr;
      const isSelected = key === selectedStr;
      const isHoliday = !!holidayMap[key];
      const stat = dayStats[key] || { worked: 0, late: 0, overtime: 0 };

      // Lógica simple de "ausencias" para visual (días con muy poca asistencia o festivo)
      const expectedWorkers = Math.max(4, Math.floor(users.length * 0.7)); // aproximado
      const hasLowAttendance = stat.worked > 0 && stat.worked < expectedWorkers * 0.5;
      const absenceIndicator = isHoliday ? 'festivo' : hasLowAttendance ? 'baja' : stat.worked > 0 ? 'trabajo' : 'sin-datos';

      return {
        date: day,
        key,
        isCurrentMonth,
        isToday,
        isSelected,
        isHoliday,
        holidayName: holidayMap[key],
        worked: stat.worked,
        late: stat.late,
        overtime: stat.overtime,
        absenceIndicator,
      };
    });
  }, [allDays, currentDate, todayStr, selectedStr, holidayMap, dayStats, users.length]);

  // Días del mes seleccionado (sin padding de semanas)
  const monthDays = useMemo(() => calendarDays.filter(d => d.isCurrentMonth), [calendarDays]);

  // Eventos destacados del mes (festivos + días con datos interesantes)
  const monthEvents = useMemo(() => {
    return monthDays
      .filter(d => d.isHoliday || d.worked > 0 || d.absenceIndicator === 'baja')
      .sort((a, b) => a.date - b.date)
      .slice(0, 12);
  }, [monthDays]);

  const selectedDayData = calendarDays.find(d => d.key === selectedStr) || {};

  const selectedDayRecords = useMemo(() => {
    return (attendanceRecords || []).filter(r => r.date === selectedStr);
  }, [attendanceRecords, selectedStr]);

  // Navegación de meses
  const goToPrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
  };

  // Seleccionar día
  const handleDayClick = (dayData) => {
    if (dayData.isCurrentMonth) {
      setSelectedDate(dayData.date);
    }
  };

  // Agregar festivo (solo admin)
  const handleAddHoliday = () => {
    if (!isAdmin || !newHolidayDate || !newHolidayName.trim()) {
      toast.error('Completa la fecha y el nombre del festivo');
      return;
    }
    const exists = holidays.some(h => h.date === newHolidayDate);
    if (exists) {
      toast.error('Ya existe un festivo en esa fecha');
      return;
    }
    setHolidays([...holidays, { date: newHolidayDate, name: newHolidayName.trim() }]);
    setNewHolidayDate('');
    setNewHolidayName('');
    setShowAddHoliday(false);
    toast.success('Festivo agregado');
  };

  // Filtrado de días para la lista de eventos
  const filteredEvents = useMemo(() => {
    if (filter === 'festivos') return monthEvents.filter(e => e.isHoliday);
    if (filter === 'trabajados') return monthEvents.filter(e => e.worked > 0);
    if (filter === 'baja') return monthEvents.filter(e => e.absenceIndicator === 'baja');
    return monthEvents;
  }, [monthEvents, filter]);

  const currentMonthLabel = `${MONTHS_ES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-[1400px] mx-auto p-6 lg:p-10 xl:p-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center text-white shadow">
              <FiCalendar size={26} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Calendario Laboral</h1>
              <p className="text-sm text-gray-500">Gestión de días festivos, ausencias y asistencia del equipo</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={goToToday} className="px-4 py-2 text-sm font-semibold border border-gray-200 bg-white hover:bg-gray-50 rounded-2xl flex items-center gap-2">
              Hoy
            </button>
            <div className="flex items-center bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <button onClick={goToPrevMonth} className="px-3 py-2 hover:bg-gray-100 text-gray-600">
                <FiChevronLeft size={18} />
              </button>
              <div className="px-6 font-bold text-gray-800 border-x border-gray-200 min-w-[200px] text-center">
                {currentMonthLabel}
              </div>
              <button onClick={goToNextMonth} className="px-3 py-2 hover:bg-gray-100 text-gray-600">
                <FiChevronRight size={18} />
              </button>
            </div>
            {isAdmin && (
              <button 
                onClick={() => setShowAddHoliday(true)} 
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-sm font-bold shadow-sm"
              >
                <FiPlus size={16} /> Agregar festivo
              </button>
            )}
          </div>
        </div>

        {/* Filtros rápidos */}
        <div className="flex items-center gap-2 mb-4 text-sm">
          <span className="text-gray-500 mr-2">Filtrar eventos:</span>
          {[
            { id: 'todos', label: 'Todos' },
            { id: 'festivos', label: 'Festivos' },
            { id: 'trabajados', label: 'Con asistencia' },
            { id: 'baja', label: 'Baja asistencia' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${filter === f.id ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Calendario principal (grid mensual) */}
          <div className="xl:col-span-8 bg-white border border-gray-100 rounded-3xl p-5 shadow-sm">
            {/* Días de la semana header */}
            <div className="grid grid-cols-7 mb-2 text-center text-xs font-bold text-gray-500 tracking-widest">
              {DAYS_ES.map((d, i) => (
                <div key={i} className="py-2">{d}</div>
              ))}
            </div>

            {/* Grid de días */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayData, index) => {
                const { date, isCurrentMonth, isToday, isSelected, isHoliday, worked, absenceIndicator } = dayData;
                const dayNum = date.getDate();

                let cellClasses = `aspect-square p-2 rounded-2xl border text-sm transition-all flex flex-col cursor-pointer ${
                  isSelected 
                    ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200' 
                    : isCurrentMonth 
                      ? 'border-gray-100 hover:border-gray-300 bg-white' 
                      : 'border-transparent text-gray-300 bg-gray-50'
                }`;

                if (isHoliday) cellClasses += ' bg-red-50 border-red-200';
                if (absenceIndicator === 'baja') cellClasses += ' bg-orange-50 border-orange-200';

                return (
                  <div 
                    key={index} 
                    onClick={() => handleDayClick(dayData)}
                    className={cellClasses}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-bold ${isToday ? 'text-emerald-600' : isCurrentMonth ? 'text-gray-800' : ''}`}>
                        {dayNum}
                      </span>
                      {isHoliday && <FiGift className="text-red-500" size={14} />}
                      {isToday && !isHoliday && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                    </div>

                    {/* Indicadores */}
                    <div className="mt-auto pt-1 text-[10px] space-y-0.5">
                      {isHoliday && (
                        <div className="text-red-600 font-bold truncate text-[9px] leading-none">{dayData.holidayName?.substring(0, 18)}</div>
                      )}
                      {worked > 0 && (
                        <div className="flex items-center gap-1 text-emerald-700">
                          <FiUsers size={10} /> <span className="font-bold tabular-nums">{worked}</span>
                        </div>
                      )}
                      {absenceIndicator === 'baja' && (
                        <div className="text-orange-600 text-[9px] font-bold">Baja asistencia</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Panel lateral: Detalle del día + Eventos */}
          <div className="xl:col-span-4 space-y-6">
            {/* Detalle del día seleccionado */}
            <div className="bg-white border border-gray-100 rounded-3xl p-5">
              <div className="font-extrabold text-lg mb-1 flex items-center gap-2">
                <FiCalendar className="text-emerald-600" />
                {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
              </div>

              {selectedDayData.isHoliday && (
                <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold mb-3">
                  <FiGift size={13} /> {selectedDayData.holidayName}
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 text-center mb-4">
                <div>
                  <div className="text-3xl font-extrabold tabular-nums text-emerald-600">{selectedDayData.worked || 0}</div>
                  <div className="text-xs text-gray-500">Trabajaron</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold tabular-nums text-orange-600">{selectedDayData.late || 0}</div>
                  <div className="text-xs text-gray-500">Tardanzas</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold tabular-nums text-sky-600">{selectedDayData.overtime || 0}</div>
                  <div className="text-xs text-gray-500">Horas extra</div>
                </div>
              </div>

              {/* Registros del día */}
              <div>
                <div className="text-xs font-bold text-gray-500 mb-2">Asistencia del día</div>
                {selectedDayRecords.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-auto text-sm">
                    {selectedDayRecords.map((rec, idx) => {
                      const u = users.find(x => x.id === rec.userId);
                      return (
                        <div key={idx} className="flex justify-between bg-gray-50 rounded-2xl px-3 py-1.5">
                          <div className="truncate">{u?.displayName || rec.userDisplayName || 'Colaborador'}</div>
                          <div className="font-mono text-xs text-gray-500">
                            {rec.entryTime || '--:--'} → {rec.exitTime || 'en curso'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 py-3">No hay registros de asistencia para este día.</div>
                )}
              </div>
            </div>

            {/* Eventos del mes */}
            <div className="bg-white border border-gray-100 rounded-3xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-extrabold text-lg">Eventos del mes</span>
                <span className="text-xs text-gray-400">{filteredEvents.length} destacados</span>
              </div>
              <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
                {filteredEvents.length > 0 ? filteredEvents.map((ev, i) => (
                  <button 
                    key={i} 
                    onClick={() => { setSelectedDate(ev.date); }}
                    className="w-full text-left flex items-center justify-between p-3 rounded-2xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30 text-sm"
                  >
                    <div>
                      <div className="font-semibold">{format(ev.date, "d 'de' MMMM", { locale: es })}</div>
                      {ev.isHoliday && <div className="text-red-600 text-xs font-bold">{ev.holidayName}</div>}
                    </div>
                    <div className="text-right">
                      {ev.worked > 0 && <div className="text-emerald-600 font-bold tabular-nums text-xs">{ev.worked} trabajando</div>}
                      {ev.isHoliday && <div className="text-red-500 text-xs">Festivo</div>}
                    </div>
                  </button>
                )) : (
                  <div className="text-sm text-gray-400 py-6 text-center">No hay eventos que coincidan con el filtro.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Leyenda y nota */}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-500">
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-red-200 rounded"></span> Días festivos</div>
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-emerald-200 rounded"></span> Con asistencia registrada</div>
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-orange-200 rounded"></span> Baja asistencia</div>
          <div className="ml-auto text-gray-400">Los datos de asistencia vienen de los registros reales del módulo de Control de Personal.</div>
        </div>

        {/* Modal agregar festivo */}
        {showAddHoliday && isAdmin && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="font-extrabold text-xl">Agregar día festivo</div>
                <button onClick={() => setShowAddHoliday(false)}><FiX size={20} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Fecha</label>
                  <input 
                    type="date" 
                    value={newHolidayDate} 
                    onChange={(e) => setNewHolidayDate(e.target.value)} 
                    className="w-full border border-gray-200 rounded-2xl px-4 py-2.5" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Nombre del festivo</label>
                  <input 
                    type="text" 
                    value={newHolidayName} 
                    onChange={(e) => setNewHolidayName(e.target.value)} 
                    placeholder="Ej. Día de la Independencia" 
                    className="w-full border border-gray-200 rounded-2xl px-4 py-2.5" 
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowAddHoliday(false)} className="flex-1 py-2.5 rounded-2xl border text-sm font-bold">Cancelar</button>
                <button onClick={handleAddHoliday} className="flex-1 py-2.5 rounded-2xl bg-emerald-600 text-white text-sm font-bold">Guardar festivo</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarioLaboral;